import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const adminApi = axios.create({
  baseURL: `${API_BASE_URL}/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Authentication
export const adminLogin = async (credentials) => {
  try {
    const response = await adminApi.post('/login', credentials);
    const { access_token, admin } = response.data;
    
    localStorage.setItem('admin_token', access_token);
    localStorage.setItem('admin_user', JSON.stringify(admin));
    
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const adminLogout = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
  window.location.href = '/admin/login';
};

export const getAdminProfile = async () => {
  try {
    const response = await adminApi.get('/profile');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Dashboard
export const getDashboardStats = async () => {
  try {
    const response = await adminApi.get('/dashboard/stats');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Consultations
export const getConsultations = async (params = {}) => {
  try {
    const response = await adminApi.get('/consultations', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const updateConsultationStatus = async (id, status) => {
  try {
    const response = await adminApi.put(`/consultations/${id}`, { status });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const deleteConsultation = async (id) => {
  try {
    const response = await adminApi.delete(`/consultations/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Transformations
export const getTransformationsAdmin = async (params = {}) => {
  try {
    const response = await adminApi.get('/transformations', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const createTransformation = async (data) => {
  try {
    const response = await adminApi.post('/transformations', data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const updateTransformation = async (id, data) => {
  try {
    const response = await adminApi.put(`/transformations/${id}`, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const deleteTransformation = async (id) => {
  try {
    const response = await adminApi.delete(`/transformations/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Recipes
export const getRecipesAdmin = async (params = {}) => {
  try {
    const response = await adminApi.get('/recipes', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const createRecipe = async (data) => {
  try {
    const response = await adminApi.post('/recipes', data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const updateRecipe = async (id, data) => {
  try {
    const response = await adminApi.put(`/recipes/${id}`, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const deleteRecipe = async (id) => {
  try {
    const response = await adminApi.delete(`/recipes/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Contacts
export const getContacts = async (params = {}) => {
  try {
    const response = await adminApi.get('/contacts', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const updateContactStatus = async (id, status) => {
  try {
    const response = await adminApi.put(`/contacts/${id}`, { status });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const deleteContact = async (id) => {
  try {
    const response = await adminApi.delete(`/contacts/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Blog API
export const getBlogs = async (params = {}) => {
  try {
    const response = await adminApi.get('/blogs', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const getBlog = async (id) => {
  try {
    const response = await adminApi.get(`/blogs/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const createBlog = async (blogData) => {
  try {
    const response = await adminApi.post('/blogs', blogData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const updateBlog = async (id, blogData) => {
  try {
    const response = await adminApi.put(`/blogs/${id}`, blogData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const deleteBlog = async (id) => {
  try {
    const response = await adminApi.delete(`/blogs/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const getBlogStats = async () => {
  try {
    const response = await adminApi.get('/blogs/stats');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Campaign Banner API
export const getCampaignBanners = async (params = {}) => {
  try {
    const response = await adminApi.get('/banners', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const getCampaignBanner = async (id) => {
  try {
    const response = await adminApi.get(`/banners/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const createCampaignBanner = async (data) => {
  try {
    const response = await adminApi.post('/banners', data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const updateCampaignBanner = async (id, data) => {
  try {
    const response = await adminApi.put(`/banners/${id}`, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const deleteCampaignBanner = async (id) => {
  try {
    const response = await adminApi.delete(`/banners/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const toggleCampaignBanner = async (id) => {
  try {
    const response = await adminApi.post(`/banners/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const getCampaignStats = async () => {
  try {
    const response = await adminApi.get('/banners/stats');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Reviews API
export const getReviewsAdmin = async (params = {}) => {
  try {
    const response = await adminApi.get('/reviews', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const createReview = async (data) => {
  try {
    const response = await adminApi.post('/reviews', data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const updateReview = async (id, data) => {
  try {
    const response = await adminApi.put(`/reviews/${id}`, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const deleteReview = async (id) => {
  try {
    const response = await adminApi.delete(`/reviews/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export default adminApi;
